/**
 * src/engine/rates.ts — rate composition, LVC, after-tax APR, and break-even
 * (§6.1–6.2, 6.12–6.13). Pure functions.
 *
 * The spread is a direct user input rather than a table lookup: securities-backed lines
 * set it once at origination (Schwab PAL from the loan value of collateral, Fidelity's
 * U.S. Bank line from the commitment amount), so the number the lender quotes is the
 * only thing the projection needs.
 */
import type { TaxFields } from '@/types';

/** Loan value of collateral for a single blended portfolio (§6.2). */
export function lvcSinglePortfolio(portfolioValue: number, blendedAdvanceRate: number): number {
  return portfolioValue * blendedAdvanceRate;
}

/** APR(t) = baseRate(t) + spreadPct (§6.1). All values in percent. */
export function aprFromBaseRate(baseRate: number, spread: number): number {
  return baseRate + spread;
}

/** afterTaxApr(t) = interestDeductible ? apr × (1 − ordinaryRate) : apr (§6.12). */
export function afterTaxApr(apr: number, tax: Pick<TaxFields, 'interestDeductible' | 'ordinaryIncomeRatePct'>): number {
  return tax.interestDeductible ? apr * (1 - tax.ordinaryIncomeRatePct / 100) : apr;
}

export interface BreakEven {
  breakEvenReturnPct: number;
  carryIsAccretive: boolean;
}

/**
 * Break-even (§6.13): the portfolio return needed to offset the after-tax loan cost.
 * Carrying the loan builds wealth when the assumed return exceeds it.
 */
export function breakEven(afterTaxAprPct: number, portfolioExpectedReturnPct: number): BreakEven {
  return {
    breakEvenReturnPct: afterTaxAprPct,
    carryIsAccretive: portfolioExpectedReturnPct > afterTaxAprPct,
  };
}
